import React from 'react';

import PropTypes from 'prop-types';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css, keyframes } from 'styled-components';

import type { CommonProps } from './constants';
import { COMMON } from './constants';
import theme from './theme';

type Size = 'xxs' | 'xs' | 's' | 'm';

type Props = CommonProps & {
  size?: Size;
};

const glowdelay = keyframes`
  0%,
  100% {
    opacity: 0.25;
  }
  30% {
    opacity: 1;
  }
`;

const spinnerSizeStyles: Record<Size, FlattenSimpleInterpolation> = {
  xxs: css`
    width: 2px;
    height: 2px;
  `,
  xs: css`
    width: 5px;
    height: 5px;
  `,
  s: css`
    width: 10px;
    height: 10px;
  `,
  m: css`
    width: 15px;
    height: 15px;
  `,
};

const sizeMarginMap: Record<Size, string> = {
  xxs: '0 2px',
  xs: '0 3px',
  s: '0 4px',
  m: '0 5px',
};

function SpinnerBase(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div role="alert" aria-busy="true" {...props}>
      <div className="dot1" />
      <div className="dot2" />
      <div className="dot3" />
    </div>
  );
}

const Spinner = styled(SpinnerBase)<Props>`
  display: block;
  text-align: center;
  ${COMMON};

  & > div {
    background-color: ${theme.colors.gray.dark};
    opacity: 0.25;
    border-radius: 100%;
    display: inline-block;
    animation: ${glowdelay} 1.4s infinite ease-in-out;
    ${({ size }: Props) => size && spinnerSizeStyles[size]};
  }

  & .dot1 {
    animation-delay: -0.32s;
  }

  & .dot2 {
    animation-delay: -0.16s;
    margin: ${({ size }: Props) => size && sizeMarginMap[size]};
  }
`;

Spinner.defaultProps = { theme };

Spinner.propTypes = {
  ...COMMON.propTypes,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  size: PropTypes.oneOf(['xxs', 'xs', 's', 'm']),
};

Spinner.displayName = 'Spinner';

export { Spinner };
